import fetchService from '../servises/fetch.service';
import { makeAutoObservable } from 'mobx';
import utilsService from '../servises/utils.service';
import userStore from './user.store';
import alarmsStore from './alarms.store';
import { enqueueMutation } from '../servises/mutation-queue.service';
import {
    getAccountStateGeneration,
    isAccountStateCurrent,
    registerAccountStateReset
} from '../servises/account-state.service';

class MongoDbStore {
    constructor() {
        makeAutoObservable(this, { tagMutationQueue: false });
    }

    mongoDbListObj = {};

    canAddNewMongoDb = false;
    requestSequence = 0;
    tagMutationQueue = new Map();

    async create(payload) {
        const accountStateGeneration = getAccountStateGeneration();
        this.requestSequence += 1;
        const response = await fetchService.authenticatedPost(`/mongo`, payload);
        if (!isAccountStateCurrent(accountStateGeneration)) {
            return null;
        }
        this.requestSequence += 1;
        const responseData = response && response.data;
        const data = responseData && responseData.data_o;
        if (!responseData || responseData.isError || !data || !data._id) {
            throw new Error(responseData?.message || 'Failed to create Mongo monitor');
        }

        this.normalize(data);
        this.mongoDbListObj[data._id] = data;
        this.mongoDbListObj = { ...this.mongoDbListObj };
        this.detectIfCanAddNewServers();
        return data;
    }

    async getMongoDb(isForce) {
        if (!isForce && Object.keys(this.mongoDbListObj).length > 0) {
            return;
        }
        const requestSequence = ++this.requestSequence;
        const response = await fetchService.authenticatedGet(`/mongo`);
        const responseData = response && response.data;
        if (!responseData || responseData.isError) {
            return;
        }
        const data = responseData.data_o === null ? [] : responseData.data_o;
        if (!Array.isArray(data)) {
            return;
        }
        if (requestSequence !== this.requestSequence) {
            return;
        }
        const incomingIds = new Set(data.map(item => item._id));
        Object.keys(this.mongoDbListObj).forEach(mongoId => {
            if (!incomingIds.has(mongoId)) {
                alarmsStore.removeServerAlarms(mongoId);
            }
        });
        data.forEach(item => {
            this.normalize(item);
        });
        this.mongoDbListObj = utilsService.normalizeApiData(data);
        this.mongoDbListObj = { ...this.mongoDbListObj };
        this.detectIfCanAddNewServers();
    }

    async updateMongoTags(id, tagId, isAdd, isServerCreated) {
        const accountStateGeneration = getAccountStateGeneration();
        this.requestSequence += 1;
        return enqueueMutation(this.tagMutationQueue, id, async () => {
            if (!isAccountStateCurrent(accountStateGeneration)) {
                return;
            }
            const response = await fetchService.authenticatedPut(`/mongo/${ id }`,
                { tagId: tagId, isAdd }
            );
            if (!isAccountStateCurrent(accountStateGeneration)) {
                return;
            }
            this.requestSequence += 1;

            const data = response.data.data_o;
            this.mongoDbListObj[data._id].tags = data.tags;
            this.mongoDbListObj = { ...this.mongoDbListObj };
        });
    }

    async updateMongo(id, name) {
        const accountStateGeneration = getAccountStateGeneration();
        this.requestSequence += 1;
        const response = await fetchService.authenticatedPut(`/mongo/${ id }`,
            { name }
        );
        if (!isAccountStateCurrent(accountStateGeneration)) {
            return;
        }
        this.requestSequence += 1;

        const data = response.data.data_o;
        this.mongoDbListObj[data._id].name = data.name;
        this.mongoDbListObj = { ...this.mongoDbListObj };
    }

    async deleteServer(serverId) {
        const accountStateGeneration = getAccountStateGeneration();
        this.requestSequence += 1;
        await fetchService.authenticatedDelete(
            `/mongo/${ serverId }`);
        if (!isAccountStateCurrent(accountStateGeneration)) {
            return;
        }
        this.requestSequence += 1;
        delete this.mongoDbListObj[serverId];
        this.mongoDbListObj = { ...this.mongoDbListObj };
        alarmsStore.removeServerAlarms(serverId);
        this.detectIfCanAddNewServers();
    }

    detectIfCanAddNewServers() {
        const serversCount = Object.keys(this.mongoDbListObj).length;
        const subscriptionServers = userStore.user.subscription.mongo || 5;

        if (serversCount < subscriptionServers) {
            this.canAddNewMongoDb = true;
        } else {
            this.canAddNewMongoDb = false;
        }
    }

    clear() {
        this.requestSequence += 1;
        this.mongoDbListObj = {};
        this.canAddNewMongoDb = false;
        this.tagMutationQueue.clear();
    }

    normalize(server) {
        server.isMongo = true;
        server.createdAtToDisplay = utilsService.dateMe(server.createdAt);
        const metric = typeof server.mongometrics === 'object' ? server.mongometrics : null;
        server.agentPushedTheData = !!metric;

        server.metric = metric;

        server.oldDataFetched = false;

        if (metric && metric.createdAt) {
            if (metric.createdAt + 2500000 < Date.now()) {
                server.oldDataFetched = true;
            }
        }

        server.totalDbUsage = 0;
        if (metric && metric.dbsStats) {
            const keys = Object.keys(metric.dbsStats);
            keys.forEach(key => {
                const db = metric.dbsStats[key];
                if (db && db.dbStats && Number.isFinite(db.dbStats.dataSize)) {
                    server.totalDbUsage += db.dbStats.dataSize;
                }
            });
        }

        server.totalDbUsageFormated = utilsService.formatBytes(server.totalDbUsage);

        server.classes = '';
        if (server.agentPushedTheData) {
            server.classes = 'normal';
        } else {
            server.classes = 'no-data';
        }
        if (server.oldDataFetched) {
            server.classes = 'old-data';
        }

        if (server.agentPushedTheData) {
            server.timeAgo = utilsService.timeAgo(metric.createdAt);
            server.totalSizeFormated = utilsService.formatBytes(metric.totalSize);
            server.totalSizeMb = metric.totalSizeMb;
            if (metric.serverStatus && metric.serverStatus.connections) {
                server.connections = metric.serverStatus.connections.current;
                server.availableConnections = metric.serverStatus.connections.available;
            }
            if (metric.serverStatus && metric.serverStatus.mem) {
                server.memResident = metric.serverStatus.mem.resident;
                server.memVirtual = metric.serverStatus.mem.virtual;
            }
            if (metric.serverStatus && metric.serverStatus.network) {
                server.networkBytesIn = utilsService.formatBytes(metric.serverStatus.network.bytesIn);
                server.networkBytesOut = utilsService.formatBytes(metric.serverStatus.network.bytesOut);
                server.networkNumRequests = metric.serverStatus.network.numRequests;
            }
            if (metric.serverStatus && metric.serverStatus.repl) {
                const hosts = metric.serverStatus.repl.hosts;
                server.numberOfHosts = Array.isArray(hosts) ? hosts.length : 0;
            }
        } else {
            server.timeAgo = 'N/A';
        }

        alarmsStore.setAlarms(server);
    }
}

const mongoDbStore = new MongoDbStore();
registerAccountStateReset(() => mongoDbStore.clear());

export default mongoDbStore;
